import React from 'react';
import Spinner from "react-spinkit";
import {connect} from "react-redux";
import * as R from 'ramda';
import ComponentNotFound from "./ComponentNotFound";

export const withLoading404 = WrappedComponent => {
  return class Loading extends React.Component {
    componentDidMount() {
      const { preload, loading, loader } = this.props;
      if (preload) preload();
      if (loading && loader) {
        loader();
      }
    }
    render() {
      const { loading, status, resource } = this.props;
      if (loading) {
        return <Spinner name='line-scale'/>;
      }
      if (status === 404) return <ComponentNotFound resource={resource}/>;
      return <WrappedComponent {...this.props}/>
    }
  }
};
export const withLoading = WrappedComponent => {
  return class Loading extends React.Component {
    componentDidMount() {
      const { loading, loader } = this.props;
      if (loading && loader) {
        loader();
      }
    }
    render() {
      const { loading } = this.props;
      if (loading) {
        return <Spinner name='line-scale'/>;
      }
      return <WrappedComponent {...this.props}/>
    }
  }
};

export const withTransparentLoading = WrappedComponent => {
  return class Loading extends React.Component {
    componentDidMount() {
      const { loader } = this.props;
      if (loader) loader();
    }
    render() {
      const { loader, ...rest } = this.props;
      return <WrappedComponent {...rest}/>
    }
  }
};

/*
a more sophisticated loading component that has built in logic for running loaders if necessary. It expects a
"cache" prop so that it can determine the loading state of the data.

the shape of cache is the following:

cache: {
  loading: boolean
  error: exception object
  fetched: moment()
}

loader - function(props, dispatch) - the loader function to call when it is determined that the data is not loaded.

the LoadingComponent is a stand-in component to show during loading. The props on the HOC will be passed through
to it.

the ErrorComponent is a stand-in component to show in case that loading resulted in an error.

there is an optional "path" parameter. In the event that this cache object is not passed as a prop but instead embedded
inside one of the existing props, this path parameter is used to drill down to it.

 */

export const withLoading2 = (loader, LoadingComponent, ErrorComponent, path, reloadOnUpdate = false) => WrappedComponent => {
  class Loading2 extends React.Component {

    componentDidMount() {
      const { __dispatch } = this.props;
      let cache = R.path(R.append('cache', path), this.props);
      if (!cache) {
        cache = {
          loading: true,
          fetched: null,
          error: null,
        };
      }
      if (cache.loading && loader) {
        loader(this.props, __dispatch);
      }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
      if (!reloadOnUpdate) return;
      const { __dispatch } = this.props;
      let cache = R.path(R.append('cache', path), this.props);
      if (!cache) {
        cache = {
          loading: true,
          fetched: null,
          error: null,
        };
      }
      if (cache.loading && loader) {
        loader(this.props, __dispatch);
      }
    }

    render() {
      const { dispatch, __dispatch, ...rest } = this.props;
      let cache = R.path(R.append('cache', path), this.props);
      if (!cache) {
        cache = {
          loading: true,
          fetched: null,
          error: null,
        };
      }
      if (!path) {
        path = []
      }
      if (typeof cache === 'undefined') {
        return <WrappedComponent {...rest}/>
      }
      if (cache.loading && LoadingComponent && React.isValidElement(LoadingComponent)) {
        return <LoadingComponent {...rest}/>
      } else if (cache.loading) {
        return null;
      } else if (cache.error && ErrorComponent) {
        return <ErrorComponent {...rest}/>
      } else {
        return <WrappedComponent {...rest}/>
      }
    }
  }
  return connect(
    null,
    (dispatch) => ({
      __dispatch: dispatch
    })
  )(Loading2);
};



// WEBPACK FOOTER //
// ./src/components/Boilerplate/deferLoaderHoc.js